import { nanoid } from 'nanoid'
import {
  ChildEventBusOptions,
  EventBusListener,
  EventBusListenerFunction,
} from './types'
import type { CoreEventType } from '../types'

export class EventBus<Event> {
  private listeners: EventBusListener<Event>[] = []

  /**
   * Adds an eventbus listener, all listeners should have a listener function and a key. The key is used by the eventbus
   * client (useEventBus, withEventBus) for removing it's listener. Each listener should have a unique key.
   */
  addListener(
    listener: EventBusListener<Event> | EventBusListenerFunction<Event>,
  ) {
    // We need to ensure backwards compatibility on this method, because phoenix
    // still adds directly to an eventbus instance.
    // TODO: Consume via the useEventBus hook at phoenix
    if ('id' in listener && 'listener' in listener) {
      this.listeners.push(listener)
    } else {
      const payload = { id: nanoid(), listener }
      this.listeners.push(payload)
    }
  }

  /**
   * Removes an event bus listener based on the id
   * @param id - id of the listener to remove
   */
  removeListener(id: string) {
    this.listeners = this.listeners.filter(
      (other) => !other.id || other.id !== id,
    )
  }

  emit(event: CoreEventType | Event) {
    this.listeners.forEach(({ listener }) => listener(event))
  }

  createChild(options: ChildEventBusOptions<Event> = {}): ChildEventBus<Event> {
    return new ChildEventBus(this, options)
  }

  dispose() {
    this.listeners = []
  }
}

/**
 * An event bus that emits events to its parent but doesn't listen,
 * allowing subscribers to receive only events from the same scope.
 *
 * Also buffers events and passes the buffer to listeners when they
 * connect until stopBuffering() is called, meaning subscribers won't
 * miss events from before they connected.
 *
 * The reason the ChildEventBus has buffering but the global EventBus
 * does not is there needs to be a mechanism to tell it to stop
 * buffering (for example once all of its child components have
 * connected) otherwise it buffers indefinitely and passes all historic
 * events to anything that connects - which could lead to unexpected
 * behaviour, for example, when navigating between pages.
 *
 * Optionally accepts a list of allowed events. This can be useful when a core
 * piece of functionality depends on receiving an event which is ordinarily
 * blocked inside a ChildEventBus. Could cause unexpected behaviour, use with
 * caution.
 */
export class ChildEventBus<Event> extends EventBus<Event> {
  public readonly parent: EventBus<Event>
  private readonly closed: boolean
  private bufferedEvents: (CoreEventType | Event)[] | null = null

  constructor(
    parent: EventBus<Event>,
    {
      bufferEvents = true,
      closed = false,
      allowedEvents,
    }: ChildEventBusOptions<Event> = {},
  ) {
    super()
    this.parent = parent
    if (bufferEvents) {
      this.bufferedEvents = []
    }
    this.closed = closed

    this.handleAllowedEvents(allowedEvents)
  }

  emit(event: CoreEventType | Event) {
    this.bufferedEvents?.push(event)
    // Emit to siblings
    super.emit(event)
    if (this.closed) {
      // Only emit globally, not to parents
      globalEventBus.emit(event)
    } else {
      // Emit to the parent EventBus
      this.parent.emit(event)
    }
  }

  addListener(
    listener: EventBusListener<Event> | EventBusListenerFunction<Event>,
  ) {
    // Ensure backwards compatibility with phoenix
    // See todo comment above
    this.bufferedEvents?.forEach((event) => {
      if ('id' in listener && 'listener' in listener) {
        listener.listener(event)
      } else {
        listener(event)
      }
    })

    super.addListener(listener)
  }

  stopBuffering() {
    this.bufferedEvents = null
  }

  /**
   * Listens to parent events and emits them within the child scope if the topic
   * appears in the list of allowed events.
   */
  private handleAllowedEvents(
    allowedEvents: ChildEventBusOptions<Event>['allowedEvents'],
  ) {
    if (!allowedEvents?.length) return

    const topics = Array.isArray(allowedEvents)
      ? allowedEvents
      : parseAllowedEvents(allowedEvents)

    this.parent.addListener((event) => {
      // TODO: typescript 4.9 doesn't like this and fixing it here breaks a massive amount of other files, investigate later
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if ('topic' in event && topics.includes(event.topic)) super.emit(event)
    })
  }
}

/**
 * Attempts to create an array from JSON. If that fails the string is assumed to
 * be comma-separated and used to form the array.
 *
 * @example
 * parseAllowedEvents('["foo", "bar"]') // ['foo', 'bar']
 *
 * @example
 * parseAllowedEvents('foo, bar') // ['foo', 'bar']
 */
const parseAllowedEvents = (allowedEvents: string) => {
  try {
    return JSON.parse(allowedEvents) as string[]
  } catch {
    return allowedEvents.replaceAll(/\s/g, '').split(',')
  }
}

export const globalEventBus = new EventBus()
